import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Typography,
  Tooltip,
  message,
  Modal,
  Select,
  Card,
  Tabs,
  Popconfirm,
  Form,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  UploadOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import api from '../config/api';
import BulkUploadDialog from '../components/BulkUploadDialog';

const { Title } = Typography;
const { TabPane } = Tabs;

const KNOWN_COUNTRY_LABELS: Record<string, string> = {
  USA: 'United States',
  Canada: 'Canada',
  'New Zealand': 'New Zealand',
  NZ: 'New Zealand',
};

function labelForStoredCountry(value: string): string {
  return KNOWN_COUNTRY_LABELS[value] || value;
}

/** Normalize services from API (text[], string, comma-separated, or legacy `{a,b}`). */
function normalizeBusinessServicesList(services: unknown): string[] {
  if (services == null) return [];
  if (Array.isArray(services)) {
    return services.map((s) => String(s).trim()).filter(Boolean);
  }
  if (typeof services === 'string') {
    const t = services.trim();
    if (!t) return [];
    if (t.startsWith('{') && t.endsWith('}')) {
      const inner = t.slice(1, -1);
      if (!inner) return [];
      return inner.split(',').map((s) => s.replace(/^"|"$/g, '').trim()).filter(Boolean);
    }
    try {
      const parsed = JSON.parse(t);
      if (Array.isArray(parsed)) return parsed.map((x) => String(x).trim()).filter(Boolean);
    } catch {
      /* ignore */
    }
    return t.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

interface Company {
  id: string;
  name: string;
  is_active: boolean;
}

interface Business {
  id: string;
  business_name: string;
  company_name?: string;
  mid: string;
  store_id?: string;
  contact_person: string;
  phone: string;
  cell_phone?: string;
  other_number?: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country?: string;
  services: string | string[];
  sales_agents?: Array<{
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  }>;
}

const Businesses: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canAddEditBusiness = usePermission('add_edit_business');

  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState<string | undefined>(undefined);
  const [countryFilter, setCountryFilter] = useState<string | undefined>(undefined);
  const [companyFilter, setCompanyFilter] = useState<string | undefined>(undefined);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100, total: 0 });
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
  const [confirmBulkText, setConfirmBulkText] = useState('');
  
  // Business Types state
  const [businessTypeForm] = Form.useForm();
  const [editingBusinessType, setEditingBusinessType] = useState<{ id: string; name: string; description?: string; is_active: boolean; created_at: string; updated_at: string } | null>(null);
  const [businessTypeModalVisible, setBusinessTypeModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('businesses');

  // Distinct countries in scope (for filter dropdown); narrows when company filter is set
  const { data: countriesFromApi = [] } = useQuery(
    ['business-countries', companyFilter, user?.id],
    async () => {
      const response = await api.get('/api/businesses/countries', {
        params: companyFilter ? { company_id: companyFilter } : {},
      });
      return (response.data?.countries || []) as string[];
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const countrySelectOptions = useMemo(() => {
    const set = new Set(countriesFromApi);
    ['USA', 'Canada', 'New Zealand'].forEach((c) => set.add(c));
    const merged = Array.from(set).sort((a, b) =>
      labelForStoredCountry(a).localeCompare(labelForStoredCountry(b))
    );
    return merged.map((c) => ({ value: c, label: labelForStoredCountry(c) }));
  }, [countriesFromApi]);

  // Fetch businesses
  const { data: businessesResp, isLoading } = useQuery(
    [
      'businesses',
      searchTerm,
      stateFilter,
      countryFilter,
      companyFilter,
      pagination.current,
      pagination.pageSize,
    ],
    async () => {
      const trimmed = (searchTerm || '').trim();
      if (trimmed.length >= 2) {
        const response = await api.get('/api/businesses/search', {
          params: {
            search: trimmed,
            limit: Math.min(pagination.pageSize, 100),
            ...(companyFilter ? { company_id: companyFilter } : {}),
            ...(stateFilter ? { state: stateFilter } : {}),
            ...(countryFilter ? { country: countryFilter } : {}),
          },
        });
        return response.data; // { businesses, total }
      }

      const params: any = {
        page: pagination.current,
        limit: Math.min(pagination.pageSize, 100),
      };

      if (stateFilter) {
        params.state = stateFilter;
      }
      if (countryFilter) {
        params.country = countryFilter;
      }
      if (companyFilter) {
        params.company_id = companyFilter;
      }

      const response = await api.get('/api/businesses', { params });
      return response.data; // { businesses, total, page, limit }
    },
    { keepPreviousData: true, staleTime: 30_000 }
  );

  const businesses = businessesResp?.businesses || [];

  const { data: companiesData, isLoading: loadingCompanies } = useQuery(
    'companies-for-business-filter',
    async () => {
      const response = await api.get('/api/companies');
      return response.data?.companies || [];
    },
    { enabled: user?.role === 'admin' }
  );

  const companies: Company[] = Array.isArray(companiesData) ? companiesData : [];

  useEffect(() => {
    if (businessesResp?.total) {
      setPagination(prev => ({ ...prev, total: businessesResp.total }));
    }
  }, [businessesResp?.total]);


  const bulkDeleteBusinesses = useMutation(
    async (ids: string[]) => {
      await api.post('/api/businesses/bulk-delete', { ids });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('businesses');
        message.success('Selected businesses deleted');
        setSelectedRowKeys([]);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to bulk delete businesses');
      }
    }
  );

  // Export functionality
  const handleExport = async () => {
    try {
      const response = await api.get('/api/businesses/export', {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'businesses_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success('Businesses exported successfully');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to export businesses');
    }
  };

  // Bulk upload - handled by BulkUploadDialog component

  // Fetch business types
  const { data: businessTypes = [], refetch: refetchBusinessTypes } = useQuery('business-types', async () => {
    const response = await api.get('/api/business-types');
    return response.data?.businessTypes || [];
  });

  // Business Types mutations
  const createBusinessType = useMutation(async (values: { name: string; description?: string; is_active?: boolean }) => {
    const response = await api.post('/api/business-types', values);
    return response.data;
  }, {
    onSuccess: () => {
      message.success('Business type created successfully');
      refetchBusinessTypes();
      setBusinessTypeModalVisible(false);
      businessTypeForm.resetFields();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to create business type');
    }
  });

  const updateBusinessType = useMutation(async ({ id, ...values }: any) => {
    const response = await api.put(`/api/business-types/${id}`, values);
    return response.data;
  }, {
    onSuccess: () => {
      message.success('Business type updated successfully');
      refetchBusinessTypes();
      setBusinessTypeModalVisible(false);
      setEditingBusinessType(null);
      businessTypeForm.resetFields();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update business type');
    }
  });

  const deleteBusinessType = useMutation(async (id: string) => {
    const response = await api.delete(`/api/business-types/${id}`);
    return response.data;
  }, {
    onSuccess: () => {
      message.success('Business type deleted successfully');
      refetchBusinessTypes();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to delete business type');
    }
  });

  // Business Types handlers
  const handleBusinessTypeSubmit = (values: { name: string; description?: string; is_active?: boolean }) => {
    if (editingBusinessType) {
      updateBusinessType.mutate({ id: editingBusinessType.id, ...values });
    } else {
      createBusinessType.mutate(values);
    }
  };

  const handleEditBusinessType = (businessType: { id: string; name: string; description?: string; is_active: boolean; created_at: string; updated_at: string }) => {
    setEditingBusinessType(businessType);
    businessTypeForm.setFieldsValue(businessType);
    setBusinessTypeModalVisible(true);
  };

  const handleAddBusinessType = () => {
    setEditingBusinessType(null);
    businessTypeForm.resetFields();
    setBusinessTypeModalVisible(true);
  };

  const handleDeleteBusinessType = (id: string) => {
    deleteBusinessType.mutate(id);
  };

  const columns: ColumnsType<Business> = [
    {
      title: 'Business Name',
      dataIndex: 'business_name',
      key: 'business_name',
      width: 200,
      ellipsis: true,
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/businesses/${record.id}`)}
          style={{ padding: 0, textAlign: 'left', whiteSpace: 'normal', height: 'auto' }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 160,
      ellipsis: true,
      render: (value: string) => value || '—',
    },
    {
      title: 'MID',
      dataIndex: 'mid',
      key: 'mid',
      width: 160,
      ellipsis: false,
      render: (text: string) => text ? (
        <Tooltip title={text}>
          <span style={{ wordBreak: 'break-all' }}>{text}</span>
        </Tooltip>
      ) : '—',
    },
    {
      title: 'Store ID',
      dataIndex: 'store_id',
      key: 'store_id',
      width: 120,
      ellipsis: true,
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (text: string) => (
        <span style={{ whiteSpace: 'nowrap' }}>{text || '—'}</span>
      ),
    },
    {
      title: 'Contact Person',
      dataIndex: 'contact_person',
      key: 'contact_person',
      width: 150,
    },
    {
      title: 'Phone',
      key: 'phones',
      width: 120,
      ellipsis: true,
      render: (_, record) => {
        const lines = [
          record.phone && `📞 ${record.phone}`,
          record.cell_phone && `📱 ${record.cell_phone}`,
          record.other_number && `☎️ ${record.other_number}`,
        ].filter(Boolean) as string[];
        const text = lines.join(' · ');
        if (!text) return '—';
        return (
          <Tooltip title={lines.join('\n')}>
            <span style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '100%' }}>{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Services',
      dataIndex: 'services',
      key: 'services',
      width: 180,
      render: (services) => {
        const serviceList = normalizeBusinessServicesList(services);

        if (serviceList.length === 0) return '—';
        
        const maxVisible = 2;
        const visible = serviceList.slice(0, maxVisible);
        const hiddenCount = Math.max(0, serviceList.length - maxVisible);
        
        return (
          <Space size={4} wrap>
            {visible.map((svc: string, idx: number) => (
              <Tag key={`${svc}-${idx}`}>{svc}</Tag>
            ))}
            {hiddenCount > 0 && (
              <Tooltip title={serviceList.join(', ')}>
                <Tag>+{hiddenCount} more</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Location',
      key: 'location',
      width: 240,
      ellipsis: false,
      onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }),
      render: (_, record) => {
        const parts = [record.address, record.city, record.state, record.zip_code, record.country]
          .filter(Boolean)
          .join(', ');
        return <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{parts || '—'}</span>;
      },
    },
  ];

  return (
    <div style={{ padding: 24, background: '#fff', minHeight: 'calc(100vh - 64px)' }}>
      <Title level={2} style={{ marginBottom: 24 }}>Businesses</Title>
      
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Businesses" key="businesses">
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        {canAddEditBusiness && (
          <Space>
            {user?.role === 'admin' && (
              <>
                <Button danger disabled={selectedRowKeys.length === 0} onClick={() => { setConfirmBulkText(''); setConfirmBulkOpen(true); }}>Delete</Button>
                <Button icon={<DownloadOutlined />} onClick={handleExport}>
                  Export
                </Button>
                <Button icon={<UploadOutlined />} onClick={() => setBulkUploadModal(true)}>
                  Bulk Upload
                </Button>
              </>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/businesses/new')}>
              Create New Business
            </Button>
            </Space>
            )}
          </div>

      {/* Search and Filters */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          placeholder="Search businesses by name, MID, contact person, or address..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: 500 }}
          allowClear
        />
        
        <Select
          placeholder="Filter by Country"
          value={countryFilter}
          onChange={(value) => {
            setCountryFilter(value);
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
          style={{ width: 220 }}
          allowClear
          showSearch
          optionFilterProp="label"
          options={countrySelectOptions}
        />

        <Select
          placeholder="Filter by State"
          value={stateFilter}
          onChange={(value) => {
            setStateFilter(value);
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
          style={{ width: 200 }}
          allowClear
        >
          <Select.Option value="AL">Alabama</Select.Option>
          <Select.Option value="AK">Alaska</Select.Option>
          <Select.Option value="AZ">Arizona</Select.Option>
          <Select.Option value="AR">Arkansas</Select.Option>
          <Select.Option value="CA">California</Select.Option>
          <Select.Option value="CO">Colorado</Select.Option>
          <Select.Option value="CT">Connecticut</Select.Option>
          <Select.Option value="DE">Delaware</Select.Option>
          <Select.Option value="FL">Florida</Select.Option>
          <Select.Option value="GA">Georgia</Select.Option>
          <Select.Option value="HI">Hawaii</Select.Option>
          <Select.Option value="ID">Idaho</Select.Option>
          <Select.Option value="IL">Illinois</Select.Option>
          <Select.Option value="IN">Indiana</Select.Option>
          <Select.Option value="IA">Iowa</Select.Option>
          <Select.Option value="KS">Kansas</Select.Option>
          <Select.Option value="KY">Kentucky</Select.Option>
          <Select.Option value="LA">Louisiana</Select.Option>
          <Select.Option value="ME">Maine</Select.Option>
          <Select.Option value="MD">Maryland</Select.Option>
          <Select.Option value="MA">Massachusetts</Select.Option>
          <Select.Option value="MI">Michigan</Select.Option>
          <Select.Option value="MN">Minnesota</Select.Option>
          <Select.Option value="MS">Mississippi</Select.Option>
          <Select.Option value="MO">Missouri</Select.Option>
          <Select.Option value="MT">Montana</Select.Option>
          <Select.Option value="NE">Nebraska</Select.Option>
          <Select.Option value="NV">Nevada</Select.Option>
          <Select.Option value="NH">New Hampshire</Select.Option>
          <Select.Option value="NJ">New Jersey</Select.Option>
          <Select.Option value="NM">New Mexico</Select.Option>
          <Select.Option value="NY">New York</Select.Option>
          <Select.Option value="NC">North Carolina</Select.Option>
          <Select.Option value="ND">North Dakota</Select.Option>
          <Select.Option value="OH">Ohio</Select.Option>
          <Select.Option value="OK">Oklahoma</Select.Option>
          <Select.Option value="OR">Oregon</Select.Option>
          <Select.Option value="PA">Pennsylvania</Select.Option>
          <Select.Option value="RI">Rhode Island</Select.Option>
          <Select.Option value="SC">South Carolina</Select.Option>
          <Select.Option value="SD">South Dakota</Select.Option>
          <Select.Option value="TN">Tennessee</Select.Option>
          <Select.Option value="TX">Texas</Select.Option>
          <Select.Option value="UT">Utah</Select.Option>
          <Select.Option value="VT">Vermont</Select.Option>
          <Select.Option value="VA">Virginia</Select.Option>
          <Select.Option value="WA">Washington</Select.Option>
          <Select.Option value="WV">West Virginia</Select.Option>
          <Select.Option value="WI">Wisconsin</Select.Option>
          <Select.Option value="WY">Wyoming</Select.Option>
        </Select>

        {user?.role === 'admin' && (
          <Select
            placeholder="Filter by Company"
            value={companyFilter}
            onChange={(value) => {
              setCompanyFilter(value);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            style={{ width: 260 }}
            allowClear
            showSearch
            optionFilterProp="label"
            loading={loadingCompanies}
            options={companies
              .filter((c) => c.is_active)
              .map((c) => ({ value: c.id, label: c.name }))}
          />
        )}
        
        <Button 
          onClick={() => {
            setStateFilter(undefined);
            setCountryFilter(undefined);
            setCompanyFilter(undefined);
            setSearchTerm('');
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          type="default"
        >
          Clear Filters
        </Button>
      </div>

      {/* Businesses Table */}
      <Table
        columns={columns}
        dataSource={businesses}
        rowKey="id"
        loading={isLoading}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
          preserveSelectedRowKeys: true,
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} businesses`,
          onChange: (page, pageSize) => {
            setPagination({ current: page, pageSize, total: pagination.total });
          },
        }}
        scroll={{ x: 1280 }}
      />

      <Modal
        title={`Confirm Bulk Delete (${selectedRowKeys.length} business(es))`}
        open={confirmBulkOpen}
        onCancel={() => setConfirmBulkOpen(false)}
        onOk={() => { bulkDeleteBusinesses.mutate(selectedRowKeys as string[]); setConfirmBulkOpen(false); }}
        okButtonProps={{ danger: true, disabled: confirmBulkText !== 'DELETE', children: 'Delete' }}
      >
        <Typography.Paragraph type="secondary">
          This action cannot be undone. Type DELETE to confirm.
        </Typography.Paragraph>
        <Input placeholder="Type DELETE" value={confirmBulkText} onChange={(e) => setConfirmBulkText(e.target.value)} />
      </Modal>

      {/* Bulk Upload Dialog with Validation */}
      <BulkUploadDialog
        open={bulkUploadModal}
        onClose={() => setBulkUploadModal(false)}
        title="Bulk Upload Businesses"
        templateUrl="/templates/business_template.csv"
        templateApiUrl="/api/businesses/template"
        uploadEndpoint="/api/businesses/bulk-upload"
        entityType="businesses"
        onSuccess={() => {
          queryClient.invalidateQueries('businesses');
          setBulkUploadModal(false);
        }}
      />
        </TabPane>

        <TabPane tab={<span><AppstoreOutlined />Business Types</span>} key="business-types">
          {user?.role === 'admin' && (
            <Card 
              title="Business Types Management" 
              extra={
                <Button type="primary" onClick={handleAddBusinessType}>
                  Add Business Type
                </Button>
              }
            >
              <Table
                dataSource={businessTypes}
                rowKey="id"
                columns={[
                  {
                    title: 'Name',
                    dataIndex: 'name',
                    key: 'name',
                  },
                  {
                    title: 'Description',
                    dataIndex: 'description',
                    key: 'description',
                    render: (text) => text || '—',
                  },
                  {
                    title: 'Status',
                    dataIndex: 'is_active',
                    key: 'is_active',
                    render: (isActive) => (
                      <span style={{ color: isActive ? 'green' : 'red' }}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    ),
                  },
                  {
                    title: 'Actions',
                    key: 'actions',
                    render: (_: any, record: any) => (
                      <Space>
                        <Button 
                          size="small" 
                          onClick={() => handleEditBusinessType(record)}
                        >
                          Edit
                        </Button>
                        <Popconfirm
                          title="Are you sure you want to delete this business type?"
                          description="This action cannot be undone."
                          onConfirm={() => handleDeleteBusinessType(record.id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button size="small" danger>
                            Delete
                          </Button>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
                pagination={{ pageSize: 10 }}
              />
            </Card>
          )}
        </TabPane>
      </Tabs>

      {/* Business Type Modal */}
      <Modal
        title={editingBusinessType ? 'Edit Business Type' : 'Add Business Type'}
        open={businessTypeModalVisible}
        onCancel={() => {
          setBusinessTypeModalVisible(false);
          setEditingBusinessType(null);
          businessTypeForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={businessTypeForm}
          layout="vertical"
          onFinish={handleBusinessTypeSubmit}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter business type name' }]}
          >
            <Input placeholder="Enter business type name" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea 
              placeholder="Enter business type description (optional)"
              rows={3}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setBusinessTypeModalVisible(false);
                setEditingBusinessType(null);
                businessTypeForm.resetFields();
              }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={createBusinessType.isLoading || updateBusinessType.isLoading}
              >
                {editingBusinessType ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Businesses;
